import {i18n} from "ui/i18n";
import Template from "ui/Template";
import Ajax from "ui/helpers/Ajax";
import ScenePreview from "ui/scene/ScenePreview";
import SceneSprites from "ui/scene/SceneSprites";
import SceneNeighbors from "ui/scene/SceneNeighbors";
import scenesListView from 'ui/desktop/pages/scenes-list/html/scenes-list.handlebars';
import listStatusView from 'ui/desktop/pages/scenes-list/html/list-status.handlebars';
import listAuthorsView from 'ui/desktop/pages/scenes-list/html/list-authors.handlebars';
import listDownloadView from 'ui/desktop/pages/scenes-list/html/list-download-button.handlebars';
import listHelperView from 'ui/desktop/pages/scenes-list/html/list-helper.handlebars';
import FatalError from "ui/components/fatal-error/FatalError";
import Spinner from "ui/components/spinner/Spinner";
import Confirm from "ui/dialogs/confirm/Confirm";
import {formatDateTime, formatFileSize} from "ui/helpers/formatters";
import SendApproveEmail from "ui/desktop/dialogs/send-email/SendApproveEmail";
import {parseAuthorName} from "ui/helpers/parsers";
import SendDeclineEmail from "ui/desktop/dialogs/send-email/SendDeclineEmail";
import ProgressDialog from "ui/dialogs/progress/ProgressDialog";
import AbstractList from "ui/desktop/pages/AbstractList";
import {CurrentUser} from "ui/CurrentUser";
import Alert from "ui/dialogs/alert/Alert";
import {playSound} from "ui/helpers/sound";

let templates = null;

/**
 * @typedef {object} T_SceneInList
 * @property {string} id
 * @property {string} name
 * @property {string} update
 * @property {string} create
 * @property {number} changes
 * @property {string[]} authors
 * @property {number} size
 * @property {boolean} finished
 * @property {boolean} rejected
 * @property {boolean} approved
 * @property {boolean} used
 * @property {boolean} helper
 */

/**
 * @typedef {T_PageElementOptions} T_ScenesListOptions
 * @property {string} type
 * @property {boolean} [compactMode]
 * @property {boolean} [pickerMode]
 * @property {function(sceneId:string)} [pickHandler]
 */

export const TYPE_GLOBAL_SCENES = 'scenes';
export const TYPE_ELEMENTS_SCENES = 'elements';
export const TYPE_CANDIDATES_SCENES = 'candidates';

/**
 * Scenes List Element
 */
export default class ScenesList extends AbstractList {
	/**
	 * Class constructor
	 *
	 * @param {T_ScenesListOptions} options
	 */
	constructor(options) {
		super(options);

		if (templates === null) {
			templates = {
				main: new Template(scenesListView),
				status: new Template(listStatusView),
				authors: new Template(listAuthorsView),
				download: new Template(listDownloadView),
				helper: new Template(listHelperView),
			};
		}

		const curUser = CurrentUser.get();

		this._element = templates.main.createElement({
			isDesktop: !options.pickerMode,
			isElements: options.type === TYPE_ELEMENTS_SCENES,
			isScenes: options.type === TYPE_GLOBAL_SCENES,
			isAdmin: curUser.isAdmin(),
		});

		/**
		 * @type {T_ScenesListOptions}
		 * @private
		 */
		this._options = options;

		/**
		 * @type {Array<T_SceneInList>}
		 * @private
		 */
		this._data = [];

		this._build().then();
	}

	/**
	 * Returns list type
	 *
	 * @returns {string}
	 */
	get type() {
		return this._options.type || TYPE_GLOBAL_SCENES;
	}

	/**
	 * @return {string}
	 * @protected
	 */
	_getRequestUrl() {
		if (CurrentUser.get().isModerator()) {
			return '/desktop/scenes-list/' + this.type;
		}

		return '/desktop/scenes-list/helpers';
	}

	/**
	 * @return {T_ListOptions}
	 * @protected
	 */
	_getListOptions() {
		const curUser = CurrentUser.get();
		return {
			columns: [
				{
					type: 'sequence'
				},
				{
					type: 'button',
					hidden: !this._options.pickerMode,
					icon: '/images/icons.svg#icon-svg-pick-scene',
					role: 'pick',
					title: i18n('list.tooltips.pick')
				},
				{
					type: 'link',
					hidden: this._options.pickerMode || !curUser.isModerator(),
					icon: '/images/icons.svg#icon-svg-pencil',
					title: i18n('list.tooltips.edit'),
					role: 'edit',
					format: (v, data) => '/editor/' + data.id
				},
				{
					type: 'button',
					icon: '/images/icons.svg#icon-svg-play',
					role: 'play',
					title: i18n('list.tooltips.play'),
				},
				{
					className: 'scenes-list__cell_name',
					key: 'name',
					sortable: true,
					title: i18n('list.name'),
				},
				{
					className: 'scenes-list__cell_status',
					title: i18n('list.status'),
					hidden: !curUser.isUser(),
					format: (v, data) => {
						return templates.status.createHtml({
							isGlobalScene: this._options.type === TYPE_GLOBAL_SCENES,
							isCandidate: this._options.type === TYPE_CANDIDATES_SCENES,
							isElement: this._options.type === TYPE_ELEMENTS_SCENES,
							isFinished: data.finished,
							inReview: !data.rejected && !data.approved,
							isRejected: data.rejected,
							isApproved: data.approved,
							isUsed: data.used,
							isModerator: curUser.isModerator() && !this._options.pickerMode,
						});
					}
				},
				{
					className: 'scenes-list__cell_helper',
					key: 'helper',
					title: i18n('list.helper'),
					sortable: true,
					hidden: !curUser.isUser(),
					format: (v, data) => {
						return templates.helper.createHtml({
							isHelper: data.helper,
							isModerator: curUser.isModerator() && !this._options.pickerMode,
						});
					}
				},
				{
					className: 'scenes-list__cell_size',
					title: i18n('list.file-size'),
					key: 'size',
					hidden: !curUser.isUser(),
					sortable: true,
					format: formatFileSize
				},
				// {
				// 	className: 'scenes-list__cell_download',
				// 	title: '',
				// 	format: (v, data) => {
				// 		return templates.download.createHtml({sceneId: data.id});
				// 	}
				// },
				{
					className: 'scenes-list__cell_date',
					key: 'update',
					sortable: true,
					title: i18n('list.update'),
					format: formatDateTime
				},
				{
					className: 'scenes-list__cell_authors',
					title: i18n('list.authors'),
					format: (v, data) => {
						return templates.authors.createHtml({
							authors: data.authors.map(parseAuthorName)
						});
					}
				},
				{
					className: 'scenes-list__cell_changes',
					hidden: this._options.compactMode,
					key: 'changes',
					sortable: true,
					title: i18n('list.changes')
				},
				{
					className: 'scenes-list__cell_date',
					hidden: this._options.compactMode,
					key: 'create',
					sortable: true,
					title: i18n('list.create'),
					format: formatDateTime
				},
				{
					type: 'button',
					hidden: this._options.compactMode || !curUser.isModerator(),
					icon: '/images/icons.svg#icon-svg-trash',
					role: 'delete',
					title: i18n('list.tooltips.delete')
				},
			]
		};
	}

	/**
	 * @protected
	 */
	_prepareTopButtons() {
		super._prepareTopButtons();
		const recalcButton = this._element.querySelector('.button[data-role="recalc"]');
		if (recalcButton) {
			recalcButton.onclick = async () => {
				const progress = new ProgressDialog({
					title: i18n('title.recalculate-scenes')
				});
				await progress.load('/desktop/recalc-scenes');
				await this.refresh();
			};
		}

		const compileButton = this._element.querySelector('.button[data-role="compile"]');
		if (compileButton) {
			compileButton.onclick = async () => {
				const progress = new ProgressDialog({
					title: i18n('title.compile-front-page')
				});
				await progress.load('/desktop/compile');
				playSound('/assets/sounds/complete.mp3');
				await new Alert(i18n('messages.front-page-updated')).getResult();
				await this.refresh();
			};
		}

		const compileTimelapseButton = this._element.querySelector('.button[data-role="compile-timelapse"]');
		if (compileTimelapseButton) {
			compileTimelapseButton.onclick = async () => {
				const progress = new ProgressDialog({
					title: i18n('title.compile-timelapse')
				});
				await progress.load('/desktop/compile-timelapse');
				playSound('/assets/sounds/complete.mp3');
				await new Alert(i18n('messages.front-page-updated')).getResult();
				await this.refresh();
			};
		}

		const compileFullsizeButton = this._element.querySelector('.button[data-role="compile-fullsize-frames"]');
		if (compileFullsizeButton) {
			compileFullsizeButton.onclick = async () => {
				const progress = new ProgressDialog({
					title: i18n('title.compile-fullsize-frames')
				});
				await progress.load('/desktop/compile-fullsize-frames');
				playSound('/assets/sounds/complete.mp3');
				await new Alert(i18n('messages.front-page-updated')).getResult();
				await this.refresh();
			};
		}
	}

	/**
	 * @param {string} role
	 * @param {T_SceneInList} data
	 * @returns {Promise<void>}
	 * @private
	 */
	async _handleButton(role, data) {
		switch (role) {
			case 'play':
				await this._previewScene(data.id);
				break;
			case 'pick':
				if (typeof this._options.pickHandler === 'function') {
					this._options.pickHandler(data.id);
				}
				break;
			case 'delete':
				if (await new Confirm(i18n('messages.confirm-delete-scene', data.name)).getResult()) {
					if (await this._callCommand(data, 'delete')) {
						this._list.deleteRowByObject(data);
					}
				}
				break;
			case 'approve':
				if (await new Confirm(i18n('messages.confirm-approve-scene', data.name)).getResult()) {
					await (new SendApproveEmail(data.name, data.authors)).getResultPromise();

					await new ProgressDialog({title: i18n('list.tooltips.approve')})
						.load('/scene/approve/' + data.id);

					data.approved = true;
					this._list.updateRow(data);
				}
				break;
			case 'decline':
				if (await new Confirm(i18n('messages.confirm-decline-scene', data.name)).getResult()) {
					await (new SendDeclineEmail(data.name, data.authors)).getResultPromise();
					if (await this._callCommand(data, 'decline')) {
						data.rejected = true;
						this._list.updateRow(data);
					}
				}
				break;
			case 'restore':
				if (await new Confirm(i18n('messages.confirm-restore-scene', data.name)).getResult()) {
					if (await this._callCommand(data, 'restore')) {
						data.rejected = false;
						data.approved = false;
						this._list.updateRow(data);
					}
				}
				break;
			case 'in-work':
					if (await this._callCommand(data, 'mark-in-work')) {
						data.finished = false;
						this._list.updateRow(data);
					}
				break;
			case 'finish':
					if (await this._callCommand(data, 'mark-finished')) {
						data.finished = true;
						this._list.updateRow(data);
					}
				break;
			case 'mark-helper-yes':
					if (await this._callCommand(data, 'mark-helper/true')) {
						data.helper = true;
						this._list.updateRow(data);
					}
				break;
			case 'mark-helper-no':
					if (await this._callCommand(data, 'mark-helper/false')) {
						data.helper = false;
						this._list.updateRow(data);
					}
				break;
		}
	}

	/**
	 * @param {string} sceneId
	 * @returns {Promise<void>}
	 * @private
	 */
	async _previewScene(sceneId) {
		Spinner.show();
		/** @type {T_ScenePreviewInput} */
		let props = await Ajax.get('/scene/preview/' + sceneId);
		let sprites = new SceneSprites();
		let neighbors = null, queue = [];
		if (props.url !== '') {
			queue.push(sprites.loadImage(props.url, props.index));
		}

		Spinner.show();

		if (props.neighbors.length > 0) {
			neighbors = new SceneNeighbors();
			queue.push(neighbors.setData(props.neighbors));
		}
		// wait all promises parallel
		await Promise.all(queue);
		Spinner.hide();
		new ScenePreview(sprites, neighbors).show();
	}

	/**
	 * @param {T_SceneInList} data
	 * @param {string} command
	 * @returns {Promise<boolean>}
	 * @private
	 */
	async _callCommand(data, command) {
		Spinner.show();
		try {
			const res = await Ajax.get(`/scene/${command}/${data.id}`);
			if (res.result === 'error') {
				FatalError.show(res.error);
				return false;
			}

			return true;
		} catch (e) {
			FatalError.show(e.message);
		} finally {
			Spinner.hide();
		}

		return false;
	}
}
